<template>
    <div class="page-container">
        <!-- Loader -->
        <div v-if="loading" class="loader-container">
            <div class="loader"></div>
        </div>

        <!-- Top Bar -->
        <div class="top-bar">
            <div class="top-bar-left">
                <h2 class="page-title">
                    <i class="tim-icons icon-single-02"></i>
                    Korisnici
                </h2>
                <span class="count-badge">{{ users.length }}</span>
            </div>
            <div class="top-bar-right">
                <base-button type="primary" @click="openForm()" class="action-btn">
                    <i class="tim-icons icon-simple-add"></i> {{ $t('users.add') }}
                </base-button>
            </div>
        </div>

        <!-- Table -->
        <div class="table-section">
            <div class="table-toolbar">
                <base-input v-model="search" :placeholder="$t('common.search') + '...'" class="search-input">
                </base-input>
            </div>

            <div class="table-container">
                <table class="custom-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ $t('users.table.username') }}</th>
                            <th>{{ $t('users.table.name') }}</th>
                            <th>E-Mail</th>
                            <th>{{ $t('users.table.role') }}</th>
                            <th>{{ $t('users.table.status') }}</th>
                            <th>{{ $t('common.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(user, index) in filteredUsers" :key="user.id">
                            <td>{{ index + 1 }}</td>
                            <td class="name-cell">{{ user.username }}</td>
                            <td>{{ user.name || '-' }}</td>
                            <td>{{ user.email || '-' }}</td>
                            <td>
                                <span :class="['role-tag', user.role === 'ADMIN' ? 'role-admin' : 'role-user']">
                                    {{ user.role }}
                                </span>
                            </td>
                            <td>
                                <span :class="['status-tag', user.isActive ? 'status-active' : 'status-inactive']">
                                    {{ user.isActive ? $t('users.table.active') : $t('users.table.inactive') }}
                                </span>
                            </td>
                            <td>
                                <div class="action-buttons-cell">
                                    <base-button type="success" icon size="sm" class="btn-link"
                                        @click="openForm(user)" :title="$t('common.edit')">
                                        <i class="tim-icons icon-pencil"></i>
                                    </base-button>
                                    <base-button type="danger" icon size="sm" class="btn-link"
                                        @click="confirmDelete(user)" :title="$t('common.delete')"
                                        :disabled="user.id === currentUserId">
                                        <i class="tim-icons icon-trash-simple"></i>
                                    </base-button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="filteredUsers.length === 0">
                            <td colspan="7" class="empty-row">
                                <i class="tim-icons icon-zoom-split"></i>
                                <p>{{ search ? $t('common.noData') : $t('common.noData') }}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Add/Edit Modal -->
        <transition name="modal-fade">
            <div v-if="showFormModal" class="modal-overlay" @click="showFormModal = false">
                <div class="modal-container" @click.stop>
                    <div class="modal-header">
                        <h3>
                            <i class="tim-icons icon-single-02"></i>
                            {{ form.id ? $t('common.edit') : $t('users.add') }}
                        </h3>
                        <button @click="showFormModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ $t('users.form.username') }} *</label>
                                <base-input v-model="form.username" placeholder="korisnik123"
                                    :disabled="!!form.id"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('users.form.name') }}</label>
                                <base-input v-model="form.name" :placeholder="$t('users.form.name')"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">E-Mail</label>
                                <base-input v-model="form.email" placeholder="email@primer.com"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('users.form.role') }} *</label>
                                <select v-model="form.role" class="form-select">
                                    <option value="USER">USER</option>
                                    <option value="ADMIN">ADMIN</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">
                                    {{ form.id ? $t('users.form.password') : $t('users.form.password') + ' *' }}
                                    <small>(min. 6)</small>
                                </label>
                                <base-input v-model="form.password" type="password"
                                    :placeholder="form.id ? $t('users.form.passwordHint') : $t('users.form.password')">
                                </base-input>
                            </div>
                            <div class="form-group" v-if="form.id">
                                <label class="form-label">{{ $t('users.table.status') }}</label>
                                <select v-model="form.isActive" class="form-select">
                                    <option :value="true">{{ $t('users.table.active') }}</option>
                                    <option :value="false">{{ $t('users.table.inactive') }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="primary" @click="saveUser()">
                            <i class="tim-icons icon-check-2"></i> {{ $t('common.save') }}
                        </base-button>
                        <base-button type="default" @click="showFormModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Delete Confirmation -->
        <transition name="modal-fade">
            <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
                <div class="modal-container modal-sm" @click.stop>
                    <div class="modal-header danger-header">
                        <h3><i class="tim-icons icon-alert-circle-exc"></i> Brisanje</h3>
                        <button @click="showDeleteModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="confirm-text">
                            {{ $t('common.delete') }}
                            <strong>"{{ deleteTarget ? deleteTarget.username : '' }}"</strong>?
                        </p>
                    </div>
                    <div class="modal-footer">
                        <base-button type="danger" @click="deleteUser()">
                            <i class="tim-icons icon-trash-simple"></i> {{ $t('common.delete') }}
                        </base-button>
                        <base-button type="default" @click="showDeleteModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.message" :class="['toast-notif', toast.type]" @click="toast.message = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.message }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'

export default {
    name: 'FiscalUsers',
    data() {
        return {
            loading: false,
            users: [],
            search: '',
            currentUserId: '',
            showFormModal: false,
            showDeleteModal: false,
            deleteTarget: null,
            form: {},
            toast: { message: '', type: 'success' }
        }
    },
    computed: {
        filteredUsers() {
            if (!this.search) return this.users
            const s = this.search.toLowerCase()
            return this.users.filter(u =>
                (u.username && u.username.toLowerCase().includes(s)) ||
                (u.name && u.name.toLowerCase().includes(s)) ||
                (u.email && u.email.toLowerCase().includes(s))
            )
        }
    },
    created() {
        const fiscalUser = JSON.parse(localStorage.getItem('fiscal_user') || '{}')
        this.currentUserId = fiscalUser.userId || ''

        // Proveri da li je ADMIN
        if (fiscalUser.role !== 'ADMIN') {
            this.$router.push('/fiscal/locations')
            return
        }

        this.getUsers()
    },
    methods: {
        getUsers() {
            this.loading = true
            axios.get('/api/v1/users').then(res => {
                this.loading = false
                this.users = res.data
            }).catch(err => {
                this.loading = false
                if (err.response && err.response.status === 403) {
                    this.showToast(this.$t('common.noData'), 'error')
                    this.$router.push('/fiscal/locations')
                } else {
                    this.showToast('Error', 'error')
                }
            })
        },

        openForm(user) {
            if (user) {
                this.form = {
                    id: user.id,
                    username: user.username,
                    name: user.name,
                    email: user.email,
                    role: user.role,
                    isActive: user.isActive,
                    password: ''
                }
            } else {
                this.form = {
                    username: '', name: '', email: '',
                    role: 'USER', password: ''
                }
            }
            this.showFormModal = true
        },

        saveUser() {
            if (!this.form.id) {
                // Novi korisnik
                if (!this.form.username || !this.form.username.trim()) {
                    this.showToast(this.$t('users.form.username') + ' required', 'error')
                    return
                }
                if (!this.form.password || this.form.password.length < 6) {
                    this.showToast(this.$t('users.form.password') + ' min 6', 'error')
                    return
                }
            } else {
                // Izmena - lozinka opciona
                if (this.form.password && this.form.password.length < 6) {
                    this.showToast(this.$t('users.form.password') + ' min 6', 'error')
                    return
                }
            }

            this.loading = true
            const isEdit = !!this.form.id

            // Za edit, pošalji samo polja koja se menjaju
            let payload = { ...this.form }
            if (isEdit && !payload.password) {
                delete payload.password
            }
            if (isEdit) {
                // isActive treba kao string za backend Map<String,String>
                payload.isActive = String(payload.isActive)
            }

            const request = isEdit
                ? axios.put('/api/v1/user/' + this.form.id, payload)
                : axios.post('/api/v1/user', payload)

            request.then(res => {
                this.loading = false
                this.showFormModal = false
                this.showToast(isEdit ? this.$t('common.save') : this.$t('common.save'), 'success')
                this.getUsers()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        confirmDelete(user) {
            if (user.id === this.currentUserId) {
                this.showToast(this.$t('users.cantDeleteSelf'), 'error')
                return
            }
            this.deleteTarget = user
            this.showDeleteModal = true
        },

        deleteUser() {
            this.loading = true
            axios.delete('/api/v1/user/' + this.deleteTarget.id).then(res => {
                this.loading = false
                this.showDeleteModal = false
                this.showToast(this.$t('common.delete'), 'success')
                this.getUsers()
            }).catch(err => {
                this.loading = false
                this.showDeleteModal = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        showToast(message, type) {
            this.toast = { message, type }
            setTimeout(() => { this.toast = { message: '', type: 'success' } }, 3000)
        }
    }
}
</script>

<style scoped>
.page-container { padding: 0; }
.loader-container {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
}
.loader {
    width: 40px; height: 40px;
    border: 4px solid #e9ecef; border-top-color: #1a73e8;
    border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.top-bar {
    display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 15px; flex-wrap: wrap; gap: 10px;
}
.top-bar-left { display: flex; align-items: center; gap: 10px; }
.top-bar-right { display: flex; gap: 8px; flex-wrap: wrap; }
.page-title {
    margin: 0; font-size: 22px; font-weight: 700;
    color: #2c3e50; display: flex; align-items: center; gap: 8px;
}
.page-title i { color: #1a73e8; }
.count-badge {
    background: #1a73e8; color: white;
    padding: 2px 10px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
}
.action-btn {
    padding: 8px 16px !important; font-size: 13px !important;
    font-weight: 600 !important; display: flex;
    align-items: center; gap: 6px;
}

.table-section {
    background: #ffffff; border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden;
}
.table-toolbar { padding: 15px; border-bottom: 1px solid #e9ecef; }
.search-input { max-width: 350px; margin-bottom: 0; }
.table-container { overflow-x: auto; }
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table th, .custom-table td {
    padding: 12px 15px; text-align: left;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50; font-size: 14px;
}
.custom-table th {
    background: #f8f9fa; font-weight: 600;
    font-size: 12px; text-transform: uppercase;
    letter-spacing: 0.3px; color: #495057;
}
.custom-table tbody tr:hover { background: #f1f5ff; }
.name-cell { font-weight: 600; color: #2c3e50; }
.role-tag {
    padding: 3px 10px; border-radius: 10px;
    font-size: 12px; font-weight: 600;
}
.role-admin { background: #fff3cd; color: #856404; }
.role-user { background: #e7f3ff; color: #1a73e8; }
.status-tag {
    padding: 3px 10px; border-radius: 10px;
    font-size: 12px; font-weight: 600;
}
.status-active { background: #d4edda; color: #155724; }
.status-inactive { background: #f8d7da; color: #721c24; }
.action-buttons-cell { display: flex; gap: 5px; }
.empty-row { text-align: center; padding: 40px !important; color: #6c757d; }
.empty-row i { font-size: 36px; display: block; margin-bottom: 10px; opacity: 0.4; }
.empty-row p { margin: 0; }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); display: flex;
    align-items: center; justify-content: center;
    z-index: 99999; backdrop-filter: blur(3px);
}
.modal-container {
    background: #ffffff; border-radius: 14px;
    max-width: 650px; width: 95%; max-height: 90vh;
    overflow: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.modal-sm { max-width: 440px; }
.modal-header {
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 18px 20px; display: flex;
    justify-content: space-between; align-items: center;
    border-radius: 14px 14px 0 0;
}
.modal-header h3 {
    margin: 0; color: white; font-size: 18px;
    font-weight: 600; display: flex; align-items: center; gap: 8px;
}
.danger-header { background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%) !important; }
.modal-close {
    background: none; border: none; color: white;
    font-size: 26px; cursor: pointer;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; transition: background 0.2s;
}
.modal-close:hover { background: rgba(255,255,255,0.2); }
.modal-body { padding: 20px; color: #2c3e50; }
.modal-footer {
    padding: 14px 20px; background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    display: flex; gap: 10px; justify-content: flex-end;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.form-group { margin-bottom: 0; }
.full-width { grid-column: 1 / -1; }
.form-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #495057; margin-bottom: 5px;
}
.form-label small { font-weight: 400; color: #6c757d; }
.form-select {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer; transition: border-color 0.2s;
}
.form-select:focus { outline: none; border-color: #1a73e8; }
.confirm-text { text-align: center; font-size: 15px; color: #2c3e50; margin: 10px 0; }
.confirm-text strong { color: #dc3545; }

.toast-notif {
    position: fixed; top: 25px; right: 25px;
    padding: 12px 20px; border-radius: 10px;
    display: flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 500;
    z-index: 100000; cursor: pointer;
    box-shadow: 0 4px 15px rgba(0,0,0,0.15); color: white;
}
.toast-notif.success { background: #28a745; }
.toast-notif.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.3s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(50px); }

@media (max-width: 768px) {
    .form-grid { grid-template-columns: 1fr; }
    .top-bar { flex-direction: column; align-items: flex-start; }
    .top-bar-right { width: 100%; }
}
</style>